import React, { useState, useEffect, useCallback } from 'react';
import {
  Save, Plus, Trash2, Youtube, Link as LinkIcon, Image as ImageIcon,
  Video, Palette, CheckCircle2, AlertCircle, Loader2, Film, ExternalLink, Sparkles
} from 'lucide-react';

// ─── Reference link type detection ───────────────────────────
// Mirrors backend /api/projects/{id}/brief sanitization so the UI
// shows the same badge the server will store. Frontend-only hint —
// backend still canonicalizes on save.
function detectType(url) {
  const u = (url || '').toLowerCase();
  if (!u) return 'link';
  if (u.includes('youtube.com') || u.includes('youtu.be')) return 'youtube';
  if (u.includes('vimeo.com')) return 'vimeo';
  if (/\.(jpg|jpeg|png|webp|gif)(\?|$)/.test(u)) return 'image';
  if (u.includes('pinterest.com') || u.includes('behance.net') || u.includes('artstation.com')) return 'mood_board';
  return 'link';
}

const TYPE_META = {
  youtube:    { Icon: Youtube,    badge: 'YouTube',    color: 'text-rose-300 bg-rose-900/30 border-rose-500/30' },
  vimeo:      { Icon: Video,      badge: 'Vimeo',      color: 'text-cyan-300 bg-cyan-900/30 border-cyan-500/30' },
  image:      { Icon: ImageIcon,  badge: 'Image',      color: 'text-emerald-300 bg-emerald-900/30 border-emerald-500/30' },
  mood_board: { Icon: Palette,    badge: 'Moodboard',  color: 'text-fuchsia-300 bg-fuchsia-900/30 border-fuchsia-500/30' },
  link:       { Icon: LinkIcon,   badge: 'Link',       color: 'text-zinc-300 bg-zinc-800/60 border-zinc-600/40' },
};

// ─── Single link row ─────────────────────────────────────────
function LinkRow({ link, index, onChange, onRemove }) {
  const meta = TYPE_META[link.type] || TYPE_META.link;
  const Icon = meta.Icon;
  return (
    <div
      className="flex items-stretch gap-2 bg-zinc-950 border border-zinc-800 rounded-sm p-2 group hover:border-zinc-700 transition-colors"
      data-testid={`brief-link-row-${index}`}
    >
      <div className={`shrink-0 w-9 h-9 rounded-sm border flex items-center justify-center ${meta.color}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0 grid grid-cols-5 gap-2">
        <input
          value={link.title}
          onChange={(e) => onChange({ ...link, title: e.target.value })}
          placeholder="Title (optional)"
          className="col-span-2 h-9 bg-zinc-900 border border-zinc-800 rounded-sm px-2 text-xs text-white focus:outline-none focus:border-violet-500/60"
          data-testid={`brief-link-title-${index}`}
        />
        <input
          value={link.url}
          onChange={(e) => {
            const url = e.target.value;
            onChange({ ...link, url, type: detectType(url) });
          }}
          placeholder="https://youtube.com/… or any reference URL"
          className="col-span-3 h-9 bg-zinc-900 border border-zinc-800 rounded-sm px-2 text-xs text-white focus:outline-none focus:border-violet-500/60 font-mono"
          data-testid={`brief-link-url-${index}`}
        />
      </div>
      <div className="shrink-0 flex items-center gap-1">
        <span className={`hidden md:inline-flex items-center px-2 h-6 rounded-sm text-[9px] font-semibold uppercase tracking-wider border ${meta.color}`}>
          {meta.badge}
        </span>
        {link.url ? (
          <a
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            className="h-9 w-9 flex items-center justify-center text-zinc-500 hover:text-cyan-400 hover:bg-zinc-900 rounded-sm transition-colors"
            title="Open link"
            data-testid={`brief-link-open-${index}`}
          >
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        ) : null}
        <button
          type="button"
          onClick={onRemove}
          className="h-9 w-9 flex items-center justify-center text-zinc-500 hover:text-rose-400 hover:bg-rose-950/40 rounded-sm transition-colors"
          title="Remove link"
          data-testid={`brief-link-remove-${index}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

// ─── Main Brief Editor ───────────────────────────────────────
export default function ProjectBriefEditor({ projectId, projectName, api, canEdit = true }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successAt, setSuccessAt] = useState('');

  const [logline, setLogline] = useState('');
  const [synopsis, setSynopsis] = useState('');
  const [script, setScript] = useState('');
  const [links, setLinks] = useState([]);
  const [initialSnapshot, setInitialSnapshot] = useState('');
  const [meta, setMeta] = useState({ updated_at: null, updated_by: null });

  const serialize = useCallback((lg, syn, sc, ls) =>
    JSON.stringify({ lg: lg || '', syn: syn || '', sc: sc || '', ls: (ls || []).map(x => ({ u: x.url, t: x.title })) })
  , []);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError('');
    try {
      const r = await api.get(`/projects/${projectId}/brief`);
      const d = r.data || {};
      setLogline(d.logline || '');
      setSynopsis(d.synopsis || '');
      setScript(d.script || '');
      const loaded = d.reference_links || [];
      setLinks(loaded);
      setMeta({ updated_at: d.updated_at || null, updated_by: d.updated_by || null });
      setInitialSnapshot(serialize(d.logline, d.synopsis, d.script, loaded));
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load project brief');
    } finally {
      setLoading(false);
    }
  }, [api, projectId, serialize]);

  useEffect(() => { load(); }, [load]);

  const dirty = !loading && serialize(logline, synopsis, script, links) !== initialSnapshot;

  const addLink = () => setLinks(prev => [...prev, { url: '', title: '', type: 'link' }]);
  const updateLink = (idx, next) => setLinks(prev => prev.map((l, i) => i === idx ? next : l));
  const removeLink = (idx) => setLinks(prev => prev.filter((_, i) => i !== idx));

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = {
        logline: logline.trim(),
        synopsis: synopsis.trim(),
        script,   // preserve formatting whitespace
        reference_links: links
          .filter(l => (l.url || '').trim())
          .map(l => ({
            url: l.url.trim(),
            title: (l.title || '').trim(),
            type: l.type || detectType(l.url),
          })),
      };
      const r = await api.put(`/projects/${projectId}/brief`, payload);
      const saved = r.data || {};
      const savedLinks = saved.reference_links || [];
      setLinks(savedLinks);
      setInitialSnapshot(serialize(saved.logline, saved.synopsis, saved.script, savedLinks));
      setSuccessAt(saved.brief_updated_at || new Date().toISOString());
      setTimeout(() => setSuccessAt(''), 4000);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to save project brief');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-zinc-500 text-xs" data-testid="brief-editor-loading">
        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading project brief…
      </div>
    );
  }

  const scriptChars = (script || '').length;
  const scriptWords = (script || '').trim() ? script.trim().split(/\s+/).length : 0;
  const linkCount = links.filter(l => (l.url || '').trim()).length;

  return (
    <div className="space-y-4" data-testid="project-brief-editor">
      {/* Header strip — motto + live cascade receipt */}
      <div className="bg-gradient-to-r from-violet-950/40 via-zinc-900 to-zinc-900 border border-violet-500/30 rounded-sm p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 min-w-0">
            <div className="shrink-0 w-10 h-10 rounded-sm bg-violet-500/20 border border-violet-500/40 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-violet-300" />
            </div>
            <div className="min-w-0">
              <h3 className="text-sm font-heading font-bold text-white flex items-center gap-2">
                Project Brief
                <span className="text-[10px] uppercase tracking-widest text-violet-300/70 font-semibold">One Data · Many Access</span>
              </h3>
              <p className="text-[11px] text-zinc-400 mt-1 leading-relaxed">
                Paste the script, add YouTube/Moodboard references and hit save. Every assigned artist, rigger, animator
                and vendor in <span className="text-violet-300">{projectName || 'this project'}</span> sees them instantly as
                raw-material chips on their dashboards — no forwarding, no duplication.
              </p>
            </div>
          </div>
          <div className="shrink-0 flex items-center gap-2">
            {successAt ? (
              <span className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-300 bg-emerald-950/50 border border-emerald-500/40 px-2 h-7 rounded-sm" data-testid="brief-saved-pill">
                <CheckCircle2 className="w-3 h-3" /> Saved & Cascaded
              </span>
            ) : dirty ? (
              <span className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-300 bg-amber-950/50 border border-amber-500/40 px-2 h-7 rounded-sm" data-testid="brief-dirty-pill">
                <AlertCircle className="w-3 h-3" /> Unsaved changes
              </span>
            ) : null}
            <button
              type="button"
              onClick={handleSave}
              disabled={!canEdit || saving || !dirty}
              className="h-8 px-4 bg-violet-600 hover:bg-violet-500 disabled:bg-zinc-800 disabled:text-zinc-500 text-white text-[11px] font-semibold rounded-sm flex items-center gap-1.5 transition-colors"
              data-testid="brief-save-btn"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              {saving ? 'Saving…' : 'Save & Cascade'}
            </button>
          </div>
        </div>
        {error ? (
          <div className="mt-3 text-[11px] text-rose-300 bg-rose-950/40 border border-rose-500/30 px-3 py-2 rounded-sm" data-testid="brief-error">
            {error}
          </div>
        ) : null}
      </div>

      {/* Logline + Synopsis grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4">
          <label className="text-[10px] text-zinc-500 uppercase tracking-wider flex items-center gap-1.5">
            <Film className="w-3 h-3" /> Logline
            <span className="text-zinc-600 normal-case">· the single-sentence hook</span>
          </label>
          <input
            value={logline}
            onChange={(e) => setLogline(e.target.value)}
            placeholder="A grieving father and his young daughter brave the forest to deliver a lost moon to the sky."
            maxLength={280}
            disabled={!canEdit}
            className="mt-2 w-full h-10 bg-zinc-950 border border-zinc-800 rounded-sm px-3 text-sm text-white focus:outline-none focus:border-violet-500/60 disabled:opacity-60"
            data-testid="brief-logline-input"
          />
          <p className="mt-1 text-[9px] text-zinc-600 text-right">{logline.length}/280</p>
        </div>

        <div className="lg:col-span-2 bg-zinc-900 border border-zinc-800 rounded-sm p-4">
          <label className="text-[10px] text-zinc-500 uppercase tracking-wider flex items-center gap-1.5">
            Synopsis
            <span className="text-zinc-600 normal-case">· 1–2 paragraphs shown at the top of every artist's brief</span>
          </label>
          <textarea
            value={synopsis}
            onChange={(e) => setSynopsis(e.target.value)}
            rows={3}
            placeholder="A short summary that gives your artists the narrative gravity — what's the film about, who are the key characters, what's the tone?"
            disabled={!canEdit}
            className="mt-2 w-full bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2 text-sm text-white focus:outline-none focus:border-violet-500/60 disabled:opacity-60 resize-y leading-relaxed"
            data-testid="brief-synopsis-input"
          />
        </div>
      </div>

      {/* Script */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4" data-testid="brief-script-card">
        <div className="flex items-center justify-between mb-2">
          <label className="text-[10px] text-zinc-500 uppercase tracking-wider">
            Script / Screenplay
            <span className="text-zinc-600 normal-case ml-1">· paste full script or pipeline-facing treatment here</span>
          </label>
          <div className="flex items-center gap-3 text-[9px] text-zinc-500">
            <span><span className="text-zinc-300 font-semibold">{scriptWords.toLocaleString()}</span> words</span>
            <span>·</span>
            <span><span className="text-zinc-300 font-semibold">{scriptChars.toLocaleString()}</span> chars</span>
          </div>
        </div>
        <textarea
          value={script}
          onChange={(e) => setScript(e.target.value)}
          rows={14}
          placeholder={'FADE IN:\n\nINT. FOREST CLEARING - NIGHT\n\nThe moon HANGS low above the treeline. A girl, FINN (9), her eyes wide with wonder, stares up…\n\n                                FINN\n                    Papa, why does the moon look so sad tonight?\n\nPaste your full script here — every animator, rigger, compositor and vendor assigned to this project will see it on their dashboard as a raw-material chip the moment you save.'}
          disabled={!canEdit}
          className="w-full bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2 text-[12px] text-zinc-100 focus:outline-none focus:border-violet-500/60 disabled:opacity-60 font-mono leading-relaxed resize-y"
          data-testid="brief-script-input"
        />
      </div>

      {/* Reference Links */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4" data-testid="brief-references-card">
        <div className="flex items-center justify-between mb-3">
          <div>
            <label className="text-[10px] text-zinc-500 uppercase tracking-wider flex items-center gap-1.5">
              <LinkIcon className="w-3 h-3" /> Reference Links
              <span className="text-zinc-600 normal-case ml-1">· YouTube · Moodboards · Vimeo · Pinterest · anything inspiring</span>
            </label>
            <p className="text-[10px] text-zinc-500 mt-1">
              <span className="text-violet-300 font-semibold">{linkCount}</span> link{linkCount === 1 ? '' : 's'} — type is auto-detected from the URL.
            </p>
          </div>
          <button
            type="button"
            onClick={addLink}
            disabled={!canEdit}
            className="h-8 px-3 bg-zinc-800 hover:bg-zinc-700 disabled:bg-zinc-900 disabled:text-zinc-600 text-zinc-200 text-[11px] font-medium rounded-sm flex items-center gap-1.5"
            data-testid="brief-add-link-btn"
          >
            <Plus className="w-3 h-3" /> Add Reference Link
          </button>
        </div>
        {links.length === 0 ? (
          <div className="py-8 text-center border-2 border-dashed border-zinc-800 rounded-sm" data-testid="brief-links-empty">
            <LinkIcon className="w-5 h-5 text-zinc-700 mx-auto" />
            <p className="text-[11px] text-zinc-500 mt-2">No references yet — click <span className="text-zinc-300">+ Add Reference Link</span> to paste your first YouTube / moodboard URL.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {links.map((link, idx) => (
              <LinkRow
                key={idx}
                link={link}
                index={idx}
                onChange={(next) => updateLink(idx, next)}
                onRemove={() => removeLink(idx)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Footer — save again for long scripts + last-saved receipt */}
      <div className="flex items-center justify-between text-[10px] text-zinc-500 pt-2">
        <span data-testid="brief-last-updated">
          {meta.updated_at ? <>Last saved {new Date(meta.updated_at).toLocaleString()}</> : 'Not saved yet — nothing has cascaded downstream.'}
        </span>
        <button
          type="button"
          onClick={handleSave}
          disabled={!canEdit || saving || !dirty}
          className="h-8 px-4 bg-violet-600 hover:bg-violet-500 disabled:bg-zinc-800 disabled:text-zinc-500 text-white text-[11px] font-semibold rounded-sm flex items-center gap-1.5 transition-colors"
          data-testid="brief-save-btn-footer"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          {saving ? 'Saving…' : 'Save & Cascade Downstream'}
        </button>
      </div>
    </div>
  );
}
